import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, of, tap } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../env';

const TOKEN_KEY = 'om_access_token';
const USER_KEY = 'UserInfo';

export interface AuthUser {
  id?: number | string;
  name?: string;
  email?: string;
  role_id?: number;
  roleName?: string;
  actual_role_id?: number;
  actualRoleName?: string;
  effectiveRoleId?: number;
  effectiveRoleName?: string;
  [key: string]: any;
}

export interface AuthResponse {
  accessToken: string;
  user: AuthUser;
}

export type LogoutReason = 'logout' | 'expired' | 'session-expired' | 'idle-timeout';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly apiUrl = environment.omApiUrl;

  constructor(private http: HttpClient, private router: Router) {}

  login(email: string, password: string): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.apiUrl}/auth/login`, { email, password })
      .pipe(tap((res) => this.persistSession(res)));
  }

  loginViaSso(ssoToken: string): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.apiUrl}/auth/sso`, { ssoToken })
      .pipe(tap((res) => this.persistSession(res)));
  }

  fetchMe(): Observable<AuthUser> {
    return this.http.get<AuthUser>(`${this.apiUrl}/auth/me`);
  }

  logout(reason: LogoutReason = 'logout'): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/logout`, {}).pipe(
      tap(() => this.finishLogout(reason)),
      catchError(() => {
        this.finishLogout(reason);
        return of(null);
      }),
    );
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  getCurrentUser(): AuthUser | null {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    // Best-effort expiry check
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload?.exp && Date.now() >= payload.exp * 1000) return false;
    } catch {
      // non-JWT token, trust presence
    }
    return true;
  }

  hasRole(roleId: number): boolean {
    const user = this.getCurrentUser();
    const roleIds = Array.isArray(user?.['role_ids']) ? user?.['role_ids'] : [user?.role_id];
    return !!user && roleIds.map(Number).includes(Number(roleId));
  }

  clearSession(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem('UserPermissions');
    localStorage.removeItem('RoleId');
    localStorage.removeItem('UserId');
    localStorage.removeItem('om_name');
  }

  finishLogout(reason: LogoutReason = 'logout'): void {
    this.clearSession();
    const queryParams =
      reason === 'idle-timeout'
        ? { reason: 'idle-timeout' }
        : reason === 'expired' || reason === 'session-expired'
          ? { reason: 'session-expired' }
          : undefined;
    this.router.navigate(['/login'], { queryParams });
  }

  private persistSession(res: AuthResponse): void {
    if (res?.accessToken) {
      localStorage.setItem(TOKEN_KEY, res.accessToken);
    }
    if (res?.user) {
      const user = this.normalizeUserRoles(res.user);
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      if (user.name) {
        localStorage.setItem('om_name', user.name);
      }
    }
  }

  private normalizeUserRoles(user: AuthUser): AuthUser {
    const primaryRoleId = Number(user.actual_role_id || user.role_id || user['RoleId'] || 0);
    const primaryRoleName = user.actualRoleName || user.roleName || user['role_name'] || (primaryRoleId ? `Role ${primaryRoleId}` : '');
    let assignedRoles = Array.isArray(user['assignedRoles']) ? user['assignedRoles'] : [];
    if (!assignedRoles.length && Array.isArray(user['AssignedRoles'])) {
      assignedRoles = user['AssignedRoles'];
    }
    if (!assignedRoles.length && primaryRoleId) {
      assignedRoles = [{ id: primaryRoleId, name: primaryRoleName }];
    }
    const rolesById = new Map<number, any>();
    assignedRoles.forEach((role: any) => {
      const id = Number(role.id ?? role.role_id ?? role.RoleId);
      if (id) rolesById.set(id, { id, name: role.name || role.roleName || `Role ${id}` });
    });
    if (primaryRoleId && !rolesById.has(primaryRoleId)) {
      rolesById.set(primaryRoleId, { id: primaryRoleId, name: primaryRoleName });
    }
    const normalizedRoles = Array.from(rolesById.values()).sort((a, b) => a.id - b.id);
    const effectiveRoleId = Number(user.effectiveRoleId || primaryRoleId || normalizedRoles[0]?.id || 0);
    const effectiveRoleName = normalizedRoles.find((role) => role.id === effectiveRoleId)?.name || primaryRoleName;

    return {
      ...user,
      role_id: effectiveRoleId || primaryRoleId,
      roleName: effectiveRoleName,
      actual_role_id: primaryRoleId,
      actualRoleName: primaryRoleName,
      effectiveRoleId,
      effectiveRoleName,
      assignedRoles: normalizedRoles,
      role_ids: normalizedRoles.map((role) => role.id),
    };
  }
}
